/**
 * Insights Page: Financial Intelligence — matches reference design.
 * Two-column layout: left = filter bar + selected insight category cards; right = AI Financial Assistant chat.
 */
import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { getCurrentUserId } from '../lib/supabase';
import { useInsights } from '../hooks/useInsights';
import { queueInsightRefresh, runInsightRefreshImmediate } from '../services/insights-refresh-service';
import { aiConfigurationService } from '../services/ai-configuration-service';
import { InsightCard, type InsightItemShape } from '../components/insights/InsightCard';
import { InsightAlertModal,
  type InsightAlertContext,
  isInsightAlertActive,
  loadActiveAlerts,
} from '../components/insights/InsightAlertModal';
import { InsightScanSummaryPanel } from '../components/insights/InsightScanSummaryPanel';
import type { InsightCategory } from '../types/insights.types';
import {
  AlertTriangle,
  TrendingUp,
  Lightbulb,
  BarChart3,
  RefreshCw,
  Loader2,
  Clock,
  X,
} from 'lucide-react';
import { submitFeedback, getFeedbackForInsights } from '../services/insight-feedback-service';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../lib/supabase';
import {
  buildCategoryNameMap,
  resolveAlertModalCategory,
  resolveInsightTitle,
  resolveInsightBody,
  formatInsightNumbers,
  resolveSectionDataConfidence,
} from '../utils/insight-display-utils';
import {
  formatCalendarYearLabel,
  formatInsightTimeRangeLabel,
  INSIGHT_TIME_RANGE_LAST_12_MONTHS,
  INSIGHT_TIME_RANGE_THIS_YEAR,
  resolveInsightTimeRangeSelection,
  resolveInsightTimeRangeSelectionFromWindow,
} from '../utils/insight-time-range';

const CATEGORIES: { id: InsightCategory; label: string; icon: React.ElementType }[] = [
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { id: 'progress', label: 'Progress', icon: TrendingUp },
  { id: 'opportunities', label: 'Opportunities', icon: Lightbulb },
  { id: 'analysis', label: 'Analysis', icon: BarChart3 },
];

const TIME_RANGE_OPTIONS = [
  { label: 'This Week', value: 7 },
  { label: 'Last Week', value: 14 },
  { label: 'This Month', value: 30 },
  { label: 'Last Month', value: 60 },
  { label: 'Last 3 months', value: 90 },
  { label: 'Last 6 months', value: 180 },
  { label: 'Last 12 months', value: INSIGHT_TIME_RANGE_LAST_12_MONTHS },
  { label: formatCalendarYearLabel(), value: INSIGHT_TIME_RANGE_THIS_YEAR },
];

const SECTION_TITLES: Record<InsightCategory, string> = {
  alerts: 'Alert Insights',
  progress: 'Progress Insights',
  opportunities: 'Opportunity Insights',
  analysis: 'Analysis',
};

const DISMISSED_INSIGHTS_KEY = 'insights-dismissed-ids';

function loadDismissedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_INSIGHTS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveDismissedIds(ids: Set<string>): void {
  try {
    sessionStorage.setItem(DISMISSED_INSIGHTS_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function InsightsPage(): React.ReactElement {
  const [userId, setUserId] = React.useState<string | null>(null);
  const [activeCategory, setActiveCategory] = React.useState<InsightCategory>('alerts');
  const [timeRangeDays, setTimeRangeDays] = React.useState(90);
  const [refreshing, setRefreshing] = React.useState(false);
  const [rateLimited, setRateLimited] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(loadDismissedIds);
  const [selectedInsight, setSelectedInsight] = React.useState<{
    item: InsightItemShape;
    category: InsightCategory;
  } | null>(null);
  const [alertModal, setAlertModal] = React.useState<InsightAlertContext | null>(null);
  const [activeAlertIds, setActiveAlertIds] = React.useState<Set<string>>(
    () => new Set(loadActiveAlerts().map((a) => a.insightId))
  );
  const [feedbackByInsightId, setFeedbackByInsightId] = React.useState<
    Map<string, 'helpful' | 'not_relevant'>
  >(new Map());
  const [categoryNameMap, setCategoryNameMap] = React.useState<Map<string, string>>(new Map());
  const autoRefreshAttempted = React.useRef(false);

  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);

  React.useEffect(() => {
    if (!userId) return;
    aiConfigurationService.getAiSettings(userId).then((res) => {
      const settings = res.data;
      if (!settings) return;
      const selected = resolveInsightTimeRangeSelection(
        settings.insightTimeRangeDays,
        settings.insightCustomStartDate,
        settings.insightCustomEndDate
      );
      if (TIME_RANGE_OPTIONS.some((o) => o.value === selected)) {
        setTimeRangeDays(selected);
      }
    });
  }, [userId]);

  React.useEffect(() => {
    if (!userId) return;
    void (async () => {
      const { data } = await supabase
        .from('categories')
        .select('id, name')
        .or(`user_id.eq.${userId},is_system.eq.true`);
      if (data) {
        setCategoryNameMap(buildCategoryNameMap(data as Array<{ id: string; name: string }>));
      }
    })();
  }, [userId]);

  const { data: sections = [], isLoading, refetch } = useInsights(userId);
  const activeSection = sections.find((s) => s.category === activeCategory);

  const allInsightIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const section of sections) {
      for (const item of section.items ?? []) {
        const id = (item as InsightItemShape).id;
        if (id) ids.add(id);
      }
    }
    return [...ids];
  }, [sections]);

  React.useEffect(() => {
    if (!userId || allInsightIds.length === 0) return;
    void getFeedbackForInsights(userId, allInsightIds).then(setFeedbackByInsightId);
  }, [userId, allInsightIds]);

  const handleRefresh = React.useCallback(async () => {
    if (!userId) return;
    setRateLimited(false);
    setRefreshError(null);
    setRefreshing(true);
    try {
      const saveResult = await aiConfigurationService.saveInsightTimeRangeSettings(
        userId,
        timeRangeDays
      );
      if (saveResult.error) {
        setRefreshError(saveResult.error.message ?? 'Could not save time range. Please try again.');
        return;
      }
      const result = await queueInsightRefresh(userId);
      if (result.queued) {
        const immediate = await runInsightRefreshImmediate();
        if (!immediate.ok) {
          setRefreshError(immediate.error ?? 'Refresh failed. Please try again.');
          return;
        }
        await refetch();
      } else if (result.reason === 'rate_limited') {
        setRateLimited(true);
      } else {
        setRefreshError(result.error ?? 'Could not start refresh. Please try again.');
      }
    } finally {
      setRefreshing(false);
    }
  }, [userId, timeRangeDays, refetch]);

  React.useEffect(() => {
    if (!userId || isLoading || autoRefreshAttempted.current || refreshing) return;
    const hasAnyData = sections.some((s) => (s.items?.length ?? 0) > 0);
    const hasRefreshed = sections.some((s) => s.refreshed_at);
    if (!hasAnyData && !hasRefreshed) {
      autoRefreshAttempted.current = true;
      void handleRefresh();
    }
  }, [userId, isLoading, sections, refreshing, handleRefresh]);

  const handleDismiss = React.useCallback((id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissedIds(next);
      return next;
    });
  }, []);

  const handleFeedback = React.useCallback(
    async (insightId: string, feedbackType: 'helpful' | 'not_relevant'): Promise<boolean> => {
      if (!userId) return false;
      const result = await submitFeedback(userId, insightId, feedbackType);
      if (result.success) {
        setFeedbackByInsightId((prev) => new Map(prev).set(insightId, feedbackType));
        return true;
      }
      return false;
    },
    [userId]
  );

  const handleInsightAction = React.useCallback((item: InsightItemShape) => {
    const action = item.action ?? 'Set Alert';
    if (action === 'Review Now') {
      window.location.href = '/transactions?filter=needs_review';
      return;
    }
    if (action === 'Set Alert' || action === 'Plan Buffer') {
      const existing = loadActiveAlerts().find((a) => a.insightId === (item.id ?? ''));
      setAlertModal({
        insightId: item.id ?? '',
        title: resolveInsightTitle(item, categoryNameMap),
        category: resolveAlertModalCategory(item, categoryNameMap),
        action,
        frequency: existing?.frequency ?? 'weekly',
      });
      return;
    }
    if (item.id) {
      setSelectedInsight({ item, category: activeCategory });
    }
  }, [activeCategory, categoryNameMap]);

  React.useEffect(() => {
    if (!selectedInsight) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedInsight(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedInsight]);

  const scanSummary =
    sections.find((s) => s.category === 'analysis')?.scan_summary ?? null;

  const getCount = (category: InsightCategory) => {
    const section = sections.find((s) => s.category === category);
    const items = (section?.items ?? []) as InsightItemShape[];
    return items.filter((item) => !dismissedIds.has(item.id ?? '')).length;
  };

  const visibleItems = ((activeSection?.items ?? []) as InsightItemShape[]).filter(
    (item) => !dismissedIds.has(item.id ?? '')
  );
  const sectionDataConfidence = resolveSectionDataConfidence(visibleItems);
  const cachedTimeRangeDays = activeSection?.time_range_days;
  const analyzedSelection = resolveInsightTimeRangeSelectionFromWindow(
    cachedTimeRangeDays ?? 90,
    scanSummary?.window_start_date,
    scanSummary?.window_end_date
  );
  const cachedRangeLabel = formatInsightTimeRangeLabel(analyzedSelection);
  const selectedRangeLabel = formatInsightTimeRangeLabel(timeRangeDays);
  const rangeIsStale =
    analyzedSelection !== timeRangeDays &&
    !isLoading &&
    !refreshing;

  return (
    <div className="space-y-4" role="main">
      <PageHeader
        title="Financial Intelligence"
        description="AI insights and analytics"
        helpSlug="insights"
      />

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <span className="font-bold text-gray-900 dark:text-gray-100">Insights</span>
          {CATEGORIES.map(({ id, label, icon: Icon }) => {
            const count = getCount(id);
            const isActive = activeCategory === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveCategory(id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium focus-ring ${
                  isActive
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200 border border-primary-200 dark:border-primary-700'
                    : 'bg-white dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                aria-pressed={isActive}
                aria-label={`${label} (${count})`}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}({count})
              </button>
            );
          })}
          <div className="flex items-center gap-2 ml-auto">
            <label htmlFor="insights-time-range" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Time Range:
            </label>
            <select
              id="insights-time-range"
              value={timeRangeDays}
              onChange={(e) => setTimeRangeDays(Number(e.target.value))}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus-ring min-w-[140px]"
              aria-label="Time range for insights"
            >
              {TIME_RANGE_OPTIONS.filter(
                (opt) =>
                  opt.value !== INSIGHT_TIME_RANGE_LAST_12_MONTHS &&
                  opt.value !== INSIGHT_TIME_RANGE_THIS_YEAR
              ).map((opt) => (
                <option key={opt.value + opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value={INSIGHT_TIME_RANGE_LAST_12_MONTHS}>Last 12 months</option>
              <option value={INSIGHT_TIME_RANGE_THIS_YEAR}>{formatCalendarYearLabel()}</option>
            </select>
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing || !userId}
              className="btn-primary btn-sm inline-flex items-center gap-1.5"
              aria-label="Refresh analysis"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden />
              )}
              Refresh Analysis
            </button>
          </div>
        </div>
      </div>

      {rangeIsStale && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-4 py-2 text-sm">
          Showing insights analysed for <strong>{cachedRangeLabel}</strong>
          {scanSummary ? (
            <>
              {' '}
              ({scanSummary.window_start_date} to {scanSummary.window_end_date})
            </>
          ) : null}
          , but you selected <strong>{selectedRangeLabel}</strong>. Click Refresh Analysis to
          update all insight tabs.
        </div>
      )}

      {rateLimited && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-4 py-2 text-sm">
          Please wait at least 30 seconds between manual refreshes.
        </div>
      )}

      {!isLoading &&
        !refreshing &&
        !rateLimited &&
        sections.length > 0 &&
        sections.some((s) => s.refreshed_at) &&
        sections.every((s) => (s.items?.length ?? 0) === 0) && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 px-4 py-2 text-sm">
            {scanSummary && scanSummary.transaction_count > 0 ? (
              <>
                Analysis scanned {scanSummary.transaction_count} transaction
                {scanSummary.transaction_count === 1 ? '' : 's'} for the last{' '}
                {TIME_RANGE_OPTIONS.find((o) => o.value === timeRangeDays)?.label?.toLowerCase() ??
                  `${timeRangeDays} days`}
                , but no insights matched your current patterns. Expand the data scanned panel below for
                details, or try a different time range.
              </>
            ) : (
              <>
                Analysis completed for the last{' '}
                {TIME_RANGE_OPTIONS.find((o) => o.value === timeRangeDays)?.label?.toLowerCase() ??
                  `${timeRangeDays} days`}
                , but no transactions were found in that period. Add transactions, then click Refresh
                Analysis.
              </>
            )}
          </div>
        )}

      {!isLoading &&
        !refreshing &&
        sections.length > 0 &&
        !sections.some((s) => s.refreshed_at) &&
        sections.every((s) => (s.items?.length ?? 0) === 0) && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 px-4 py-2 text-sm">
            No insights yet. Click Refresh Analysis to analyse your transactions for the selected
            time range.
          </div>
        )}

      {scanSummary && !isLoading && (
        <InsightScanSummaryPanel summary={scanSummary} />
      )}

      {refreshError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-2 text-sm flex items-center justify-between gap-2">
          <span>{refreshError}</span>
          <button
            type="button"
            onClick={() => setRefreshError(null)}
            className="text-red-600 dark:text-red-300 hover:underline"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {SECTION_TITLES[activeCategory]}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Intelligent analysis of your financial patterns
                {cachedRangeLabel ? (
                  <>
                    {' '}
                    · Analysed for <strong>{cachedRangeLabel}</strong>
                  </>
                ) : null}
                {sectionDataConfidence != null ? (
                  <>
                    {' '}
                    · Data confidence <strong>{sectionDataConfidence}%</strong>
                  </>
                ) : null}
              </p>
            </div>
            <div className="p-4">
              {isLoading || (refreshing && visibleItems.length === 0) ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-label="Loading insights" />
                </div>
              ) : visibleItems.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8">
                  {activeCategory === 'alerts' && 'No alerts right now. Your finances look on track.'}
                  {activeCategory === 'progress' &&
                    'No goals in progress. Add goals to see progress here.'}
                  {activeCategory === 'opportunities' &&
                    'No opportunities at the moment. Check back after more data.'}
                  {activeCategory === 'analysis' &&
                    'Insufficient data for analysis. Keep recording transactions.'}
                </p>
              ) : (
                <ul className="space-y-4">
                  {visibleItems.map((item, i) => (
                    <li key={item.id ?? i}>
                      <InsightCard
                        item={item}
                        category={activeCategory}
                        onViewDetails={(id) => {
                          const it = visibleItems.find((x) => (x.id ?? '') === id);
                          if (it) setSelectedInsight({ item: it, category: activeCategory });
                        }}
                        onDismiss={handleDismiss}
                        onFeedback={handleFeedback}
                        onAction={handleInsightAction}
                        onRefresh={() => void handleRefresh()}
                        refreshedAt={activeSection?.refreshed_at ?? null}
                        userId={userId}
                        categoryNameMap={categoryNameMap}
                        initialFeedback={item.id ? feedbackByInsightId.get(item.id) ?? null : null}
                        alertActive={item.id ? activeAlertIds.has(item.id) || isInsightAlertActive(item.id) : false}
                      />
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" aria-hidden />
                  Insights last updated:{' '}
                  {activeSection?.refreshed_at
                    ? formatDistanceToNow(new Date(activeSection.refreshed_at), { addSuffix: true })
                    : 'never'}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <RefreshCw className="h-4 w-4" aria-hidden />
                  Refresh rate: Manual Only
                </span>
                <button
                  type="button"
                  onClick={() => void handleRefresh()}
                  disabled={refreshing || !userId}
                  className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline focus-ring font-medium disabled:opacity-50"
                  aria-label="Refresh analysis"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-4 w-4" aria-hidden />
                  )}
                  Refresh Analysis
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedInsight && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="insight-detail-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 id="insight-detail-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {resolveInsightTitle(selectedInsight.item, categoryNameMap)}
              </h2>
              <button
                type="button"
                onClick={() => setSelectedInsight(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus-ring"
                aria-label="Close insight details"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4 flex-1">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {resolveInsightBody(selectedInsight.item, categoryNameMap)}
              </p>
              {selectedInsight.item.estimated_impact ??
              selectedInsight.item.potential_impact ??
              selectedInsight.item.impact ? (
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  Impact:{' '}
                  {formatInsightNumbers(
                    selectedInsight.item.estimated_impact ??
                      selectedInsight.item.potential_impact ??
                      selectedInsight.item.impact
                  )}
                </p>
              ) : null}
              {Array.isArray(selectedInsight.item.suggested_next_steps) &&
                selectedInsight.item.suggested_next_steps.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Suggested next steps
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {selectedInsight.item.suggested_next_steps.map((step, i) => (
                        <li key={i}>{formatInsightNumbers(step)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {Array.isArray(selectedInsight.item.recommended_actions) &&
                selectedInsight.item.recommended_actions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Recommended actions
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {selectedInsight.item.recommended_actions.map((step, i) => (
                        <li key={i}>{formatInsightNumbers(step)}</li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      <InsightAlertModal
        open={alertModal != null}
        context={alertModal}
        onClose={() => setAlertModal(null)}
        onSaved={(alert) => {
          setActiveAlertIds((prev) => new Set(prev).add(alert.insightId));
        }}
      />
    </div>
  );
}
